import ReactMarkdown from 'react-markdown';

import { preprocessMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';

/**
 * Renders scraped job copy (description, requirements) as Markdown.
 *
 * Job descriptions are stored as Markdown — the extraction layer organises them
 * under `##` section headings — so showing the raw string as preformatted text
 * would surface the heading and bullet syntax to the reviewer.
 */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn('text-sm leading-relaxed text-foreground/90', className)}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground first:mt-0">{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground first:mt-0">{children}</h3>
          ),
          h3: ({ children }) => (
            <h4 className="mb-1.5 mt-4 text-[13px] font-semibold text-foreground first:mt-0">{children}</h4>
          ),
          p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {children}
            </a>
          ),
          hr: () => <hr className="my-4 border-border" />,
        }}
      >
        {preprocessMarkdown(children)}
      </ReactMarkdown>
    </div>
  );
}
